<template>
  <v-container fluid class="settings-page pa-3 pa-md-6">
    <v-dialog v-model="dialogImageLogo" persistent scrollable max-width="800px">
      <v-card class="rounded-lg overflow-hidden">
        <v-toolbar dark color="primary" flat>
          <v-toolbar-title>{{ $t('recortar_imagen') }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogImageLogo = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pa-4 pa-md-6 cropper-container">
          <clipper-basic mode="normal" class="basic lg" ref="clipperImageLogo" :src="imageFrontURL">
          </clipper-basic>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="dialogImageLogo = false">{{ $t('cancel') }}</v-btn>
          <v-btn color="primary" @click="getCutingLogo()">
            <v-icon left>mdi-crop</v-icon>{{ $t('recortar') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card class="settings-hero rounded-lg mb-6 overflow-hidden" elevation="0">
      <v-card-text class="pa-5 pa-md-7">
        <div class="d-flex flex-column flex-sm-row align-sm-center">
          <v-avatar color="white" size="64" class="primary--text elevation-2 mr-sm-5 mb-4 mb-sm-0">
            <v-icon color="primary" size="34">mdi-cog-outline</v-icon>
          </v-avatar>
          <div class="white--text">
            <h1 class="text-h4 font-weight-bold mb-1">{{ $t('configuracion') }}</h1>
            <p class="mb-2 settings-hero__description">{{ $t('settings_description') }}</p>
            <div v-if="profileData.first_name" class="d-flex flex-wrap align-center text-body-2">
              <v-icon small dark class="mr-1">mdi-account-circle-outline</v-icon>
              <span>{{ profileData.first_name }} {{ profileData.last_name }}</span>
              <span v-if="profileData.email" class="mx-2">·</span>
              <span>{{ profileData.email }}</span>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <v-row align="stretch">
      <v-col cols="12" md="6">
        <v-card class="settings-card rounded-lg" outlined>
          <div class="section-header">
            <v-avatar color="blue lighten-5" size="44" class="mr-3">
              <v-icon color="primary">mdi-shield-lock-outline</v-icon>
            </v-avatar>
            <div>
              <h2 class="text-h6 font-weight-bold mb-0">{{ $t('account_security') }}</h2>
              <p class="caption grey--text text--darken-1 mb-0">{{ $t('password_description') }}</p>
            </div>
          </div>
          <v-divider></v-divider>
          <v-card-text class="pa-5">
            <form @submit.prevent="changePassword()">
              <v-text-field
                v-model="currentPassword"
                autocomplete="current-password"
                name="currentPassword"
                prepend-inner-icon="mdi-lock-outline"
                :label="$t('current_password')"
                :type="showCurrentPassword ? 'text' : 'password'"
                :append-icon="showCurrentPassword ? 'mdi-eye-off' : 'mdi-eye'"
                :error-messages="errores.current_password"
                outlined
                dense
                @click:append="showCurrentPassword = !showCurrentPassword"
              ></v-text-field>
              <ValidationObserver v-slot="{ invalid }" ref="observer">
                <ValidationProvider :rules="'required|password:@confirm'" v-slot="{ errors }">
                  <v-text-field
                    v-model="password"
                    autocomplete="new-password"
                    name="password"
                    prepend-inner-icon="mdi-lock-plus-outline"
                    :label="$t('new_password')"
                    :type="showNewPassword ? 'text' : 'password'"
                    :append-icon="showNewPassword ? 'mdi-eye-off' : 'mdi-eye'"
                    :error-messages="errors[0] ? errors[0] : errores.new_password"
                    outlined
                    dense
                    @click:append="showNewPassword = !showNewPassword"
                  ></v-text-field>
                </ValidationProvider>
                <ValidationProvider name="confirm" :rules="'required'" v-slot="{ errors }">
                  <v-text-field
                    v-model="confirmedPassword"
                    autocomplete="new-password"
                    name="confirmedPassword"
                    prepend-inner-icon="mdi-lock-check-outline"
                    :label="$t('password_new_confirm')"
                    :type="showConfirmedPassword ? 'text' : 'password'"
                    :append-icon="showConfirmedPassword ? 'mdi-eye-off' : 'mdi-eye'"
                    :error-messages="errors[0]"
                    outlined
                    dense
                    @click:append="showConfirmedPassword = !showConfirmedPassword"
                  ></v-text-field>
                </ValidationProvider>
                <div class="d-flex justify-end">
                  <v-btn color="primary" type="submit" :loading="loadingPassword" :disabled="invalid" depressed>
                    <v-icon left small>mdi-content-save-outline</v-icon>{{ $t('save_changes') }}
                  </v-btn>
                </div>
              </ValidationObserver>
            </form>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card class="settings-card rounded-lg" outlined>
          <div class="section-header">
            <v-avatar color="purple lighten-5" size="44" class="mr-3">
              <v-icon color="purple">mdi-bell-outline</v-icon>
            </v-avatar>
            <div>
              <h2 class="text-h6 font-weight-bold mb-0">{{ $t('notification_preferences') }}</h2>
              <p class="caption grey--text text--darken-1 mb-0">{{ $t('notification_description') }}</p>
            </div>
          </div>
          <v-divider></v-divider>
          <v-card-text class="pa-5">
            <form @submit.prevent="changeNotifications()">
              <div class="setting-option mb-3">
                <div class="d-flex align-center">
                  <v-icon color="primary" class="mr-3">mdi-message-text-outline</v-icon>
                  <div>
                    <div class="font-weight-medium">{{ $t('canSendSMS') }}</div>
                    <div class="caption grey--text">{{ $t('sms_notification_description') }}</div>
                  </div>
                </div>
                <v-switch v-model="canSMS" color="primary" hide-details class="mt-0"></v-switch>
              </div>
              <div class="setting-option">
                <div class="d-flex align-center">
                  <v-icon color="primary" class="mr-3">mdi-email-outline</v-icon>
                  <div>
                    <div class="font-weight-medium">{{ $t('canSendEmail') }}</div>
                    <div class="caption grey--text">{{ $t('email_notification_description') }}</div>
                  </div>
                </div>
                <v-switch v-model="canEmail" color="primary" hide-details class="mt-0"></v-switch>
              </div>
              <div class="d-flex justify-end mt-5">
                <v-btn color="primary" type="submit" :loading="loadingNotification" depressed>
                  <v-icon left small>mdi-content-save-outline</v-icon>{{ $t('save_changes') }}
                </v-btn>
              </div>
            </form>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col v-if="profileData.role === 1" cols="12" lg="7">
        <v-card class="settings-card rounded-lg" outlined>
          <div class="section-header">
            <v-avatar color="teal lighten-5" size="44" class="mr-3">
              <v-icon color="teal">mdi-image-outline</v-icon>
            </v-avatar>
            <div>
              <h2 class="text-h6 font-weight-bold mb-0">{{ $t('cambiar_wallpaper') }}</h2>
              <p class="caption grey--text text--darken-1 mb-0">{{ $t('wallpaper_description') }}</p>
            </div>
          </div>
          <v-divider></v-divider>
          <v-card-text class="pa-5">
            <form @submit.prevent="changeWallpaper()">
              <div class="wallpaper-preview mb-4">
                <v-img v-if="resultLogoURL" :src="resultLogoURL" height="260" class="rounded-lg">
                  <template #placeholder>
                    <v-row class="fill-height ma-0" align="center" justify="center">
                      <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    </v-row>
                  </template>
                </v-img>
                <div v-else class="wallpaper-empty d-flex flex-column align-center justify-center">
                  <v-icon size="52" color="grey lighten-1">mdi-image-plus-outline</v-icon>
                  <span class="grey--text mt-2">{{ $t('choose_new_wallpaper') }}</span>
                </div>
              </div>
              <v-alert v-if="errores.wallpaper" type="error" outlined dense>
                {{ errores.wallpaper }}
              </v-alert>
              <div class="d-flex flex-column flex-sm-row justify-space-between align-sm-center">
                <v-btn outlined color="primary" class="mb-3 mb-sm-0">
                  <clipper-upload
                    v-model="imageFrontURL"
                    accept="image/*"
                    @input="dialogImageLogo = true"
                  >
                    <v-icon left>mdi-upload</v-icon>{{ $t('subir_imagen') }}
                  </clipper-upload>
                </v-btn>
                <v-btn
                  color="primary"
                  type="submit"
                  depressed
                  :disabled="resultLogoURL === null || resultLogoURL.length < 500"
                  :loading="loadingWallpaper"
                >
                  <v-icon left small>mdi-content-save-outline</v-icon>{{ $t('save_changes') }}
                </v-btn>
              </div>
            </form>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col v-if="profileData.role === 1" cols="12" lg="5">
        <v-card class="settings-card rounded-lg" outlined>
          <div class="section-header">
            <v-avatar color="orange lighten-5" size="44" class="mr-3">
              <v-icon color="orange darken-2">mdi-file-document-outline</v-icon>
            </v-avatar>
            <div>
              <h2 class="text-h6 font-weight-bold mb-0">{{ $t('rulebook') }}</h2>
              <p class="caption grey--text text--darken-1 mb-0">{{ $t('rulebook_description') }}</p>
            </div>
          </div>
          <v-divider></v-divider>
          <v-card-text class="pa-5">
            <form @submit.prevent="changeRulebook()">
              <v-alert v-if="file_aux" color="blue lighten-5" text icon="mdi-file-pdf-box" class="mb-5">
                <div class="caption grey--text text--darken-1">{{ $t('current_file') }}</div>
                <div class="font-weight-medium text-truncate">{{ file_aux.name }}</div>
              </v-alert>
              <v-file-input
                v-model="file"
                :rules="rules"
                chips
                show-size
                accept=".pdf"
                outlined
                prepend-inner-icon="mdi-file-pdf-box"
                prepend-icon=""
                :placeholder="$t('selecciona_archivo_pdf')"
                :label="$t('archivo')"
                :error-messages="errores.file"
              ></v-file-input>
              <div class="d-flex justify-end">
                <v-btn
                  color="primary"
                  type="submit"
                  depressed
                  :loading="loadingRulebook"
                  :disabled="file === null || file === file_aux"
                >
                  <v-icon left small>mdi-cloud-upload-outline</v-icon>
                  {{ file_aux === null ? $t('agregar') : $t('modificar') }}
                </v-btn>
              </div>
            </form>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col v-if="profileData.role === 2 || profileData.role === 3" cols="12" md="6">
        <v-card class="settings-card rounded-lg" outlined>
          <div class="section-header">
            <v-avatar color="green lighten-5" size="44" class="mr-3">
              <v-icon color="green darken-1">mdi-qrcode</v-icon>
            </v-avatar>
            <div>
              <h2 class="text-h6 font-weight-bold mb-0">{{ $t('msg_mi_codigo') }}</h2>
              <p class="caption grey--text text--darken-1 mb-0">{{ $t('access_code_description') }}</p>
            </div>
          </div>
          <v-divider></v-divider>
          <v-card-text class="pa-5 text-center">
            <div class="qr-frame mx-auto mb-4">
              <v-img
                :src="'https://api.qrserver.com/v1/create-qr-code/?data=' + profileData.access_code + '&ecc=M&margin=20&size=400x400'"
                width="230"
                height="230"
              ></v-img>
            </div>
            <v-btn color="primary" :loading="loadingWallpaper" @click="downloadMyAccessCode()" depressed>
              <v-icon left>mdi-download</v-icon>{{ $t('download_credential') }}
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col v-if="profileData.role === 2" cols="12" md="6">
        <v-card class="settings-card rounded-lg" outlined>
          <div class="section-header">
            <v-avatar color="cyan lighten-5" size="44" class="mr-3">
              <v-icon color="cyan darken-2">mdi-palm-tree</v-icon>
            </v-avatar>
            <div class="flex-grow-1">
              <h2 class="text-h6 font-weight-bold mb-0">{{ $t('vacation_mode') }}</h2>
              <p class="caption grey--text text--darken-1 mb-0">{{ $t('vacation_mode_description') }}</p>
            </div>
            <v-switch v-model="vacation_mode" color="primary" hide-details class="mt-0"></v-switch>
          </div>
          <v-divider></v-divider>
          <v-card-text class="pa-5">
            <form @submit.prevent="changeVacationMode()">
              <v-alert :type="vacation_mode ? 'info' : 'warning'" text dense class="mb-5">
                {{ vacation_mode ? $t('vacation_mode_active') : $t('vacation_mode_inactive') }}
              </v-alert>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-menu
                    v-model="menuCalendar"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    max-width="290px"
                    min-width="290px"
                  >
                    <template #activator="{ on, attrs }">
                      <v-text-field
                        v-model="computedDateFormatted"
                        :label="$t('fecha_inicial')"
                        outlined
                        dense
                        :disabled="!vacation_mode"
                        prepend-inner-icon="mdi-calendar"
                        :error-messages="errores.vacation_mode_initial_date"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="date"
                      no-title
                      :min="getCurrentDate()"
                      :locale="$t('region')"
                      @input="menuCalendar = false"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-menu
                    v-model="menuCalendarExpiration"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    max-width="290px"
                    min-width="290px"
                  >
                    <template #activator="{ on, attrs }">
                      <v-text-field
                        v-model="computedDateExpirationFormatted"
                        :label="$t('finish_date')"
                        outlined
                        dense
                        :disabled="!vacation_mode"
                        prepend-inner-icon="mdi-calendar"
                        :error-messages="errores.vacation_mode_final_date"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="dateExpiration"
                      no-title
                      :min="getCurrentDate()"
                      :locale="$t('region')"
                      @input="menuCalendarExpiration = false"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
              <div class="text-subtitle-2 font-weight-bold mb-2">{{ $t('vacation_access_settings') }}</div>
              <div class="access-options">
                <v-switch
                  v-model="allow_access_visitors"
                  :label="$t('allow_access_visitors')"
                  :disabled="!vacation_mode"
                  color="primary"
                  hide-details
                ></v-switch>
                <v-switch
                  v-model="allow_access_residents"
                  :label="$t('allow_access_residents')"
                  :disabled="!vacation_mode"
                  color="primary"
                  hide-details
                ></v-switch>
                <v-switch
                  v-model="allow_access_workers"
                  :label="$t('allow_access_workers')"
                  :disabled="!vacation_mode"
                  color="primary"
                  hide-details
                ></v-switch>
              </div>
              <div class="d-flex justify-end mt-5">
                <v-btn color="primary" type="submit" :loading="loadingVacationMode" depressed>
                  <v-icon left small>mdi-content-save-outline</v-icon>{{ $t('save_changes') }}
                </v-btn>
              </div>
            </form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from 'axios'
import store from '@/store'
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate'
import Vue from 'vue'
import VuejsClipper from 'vuejs-clipper'
import JSPDF from 'jspdf'
import { mapGetters, mapState } from 'vuex'

extend('password', {
  params: ['target'],
  validate (value, { target }) {
    return value === target
  },
  message: 'Password confirmation does not match'
})

Vue.component('ValidationProvider', ValidationProvider)
Vue.component('ValidationObserver', ValidationObserver)
Vue.use(VuejsClipper, {
  components: {
    clipperBasic: 'clipper-basic',
    clipperFixed: 'clipper-fixed',
    clipperUpload: 'clipper-upload'
  }
})

export default {
  name: 'settings',
  data: () => ({
    currentPassword: '',
    password: '',
    confirmedPassword: '',
    errores: [],
    showCurrentPassword: false,
    showNewPassword: false,
    showConfirmedPassword: false,
    profileData: {},
    canSMS: null,
    canEmail: null,
    vacation_mode: null,
    allow_access_visitors: null,
    allow_access_residents: null,
    allow_access_workers: null,
    loadingPassword: false,
    loadingNotification: false,
    loadingRulebook: false,
    loadingVacationMode: false,
    dialogImageLogo: false,
    imageFrontURL: '',
    resultLogoURL: '',
    menuCalendar: false,
    dateExpiration: '',
    menuCalendarExpiration: false,
    date: '',
    file: null,
    file_aux: null,
    rules: [
      value => !!value || 'Archivo requerido.'
    ],
    loadingWallpaper: false
  }),
  computed: {
    computedDateFormatted () {
      return this.formatDate(this.date)
    },
    computedDateExpirationFormatted () {
      return this.formatDate(this.dateExpiration)
    },
    ...mapState(['language']),
    ...mapGetters(['getProfile'])
  },
  created () {
    this.getProfileData()
    this.getRulebook()
  },
  methods: {
    getCurrentDate () {
      var today = new Date()
      var dd = today.getDate()
      var mm = today.getMonth() + 1
      var yyyy = today.getFullYear()

      if (dd < 10) {
        dd = '0' + dd
      }
      if (mm < 10) {
        mm = '0' + mm
      }
      today = yyyy + '-' + mm + '-' + dd
      return today
    },
    formatDate (date) {
      if (!date) return null

      const [year, month, day] = date.split('-')
      return `${day}/${month}/${year}`
    },
    changePassword () {
      this.errores = []
      this.loadingPassword = true
      const config = {
        method: 'PATCH',
        url: 'https://clubtest.intrare.app/users/restore_password/',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': this.language,
          'authorization': 'Token ' + store.getters.getToken
        },
        data: {
          current_password: this.currentPassword,
          new_password: this.password
        }
      }
      axios(config)
        .then(ok => {
          this.loadingPassword = false
          this.$store.commit('snackbar_activate_true')
          this.$store.commit('snackbar_change_message', this.$t('msg_cambio_password'))
          setTimeout(this.clear, 3000)
        })
        .catch(error => {
          this.loadingPassword = false
          if (error.response.status === 400 && typeof error.response.data === 'object' && error.response.data) {
            if (error.response.data.non_field_errors) {
              this.errores.new_password = []
              for (var i = 0; i < error.response.data.non_field_errors.length; i++) {
                this.errores.new_password += error.response.data.non_field_errors[i]
              }
            } else {
              this.errores = error.response.data
            }
          }
        })
    },
    clear () {
      this.$refs.observer.reset()
      this.cleanFields()
      this.errores = []
      this.$store.commit('snackbar_activate_false')
    },
    cleanFields () {
      this.currentPassword = ''
      this.password = ''
      this.confirmedPassword = ''
    },
    getProfileData () {
      const config = {
        method: 'get',
        url: 'https://clubtest.intrare.app/users/me/',
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        }
      }
      axios(config)
        .then(response => {
          this.profileData = response.data
          this.resultLogoURL = response.data.wallpaper
          this.canSMS = this.profileData.can_sms
          this.canEmail = this.profileData.can_email
          this.vacation_mode = this.profileData.vacation_mode
          this.allow_access_visitors = this.profileData.allow_access_visitors
          this.allow_access_residents = this.profileData.allow_access_residents
          this.allow_access_workers = this.profileData.allow_access_workers
          this.date = this.profileData.vacation_mode_initial_date
          this.dateExpiration = this.profileData.vacation_mode_final_date
        })
        .catch(error => {
          console.log(error)
        })
    },
    getRulebook () {
      const config = {
        method: 'get',
        url: 'https://clubtest.intrare.app/housing_unit/rulebook/',
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        }
      }
      axios(config)
        .then(response => {
          console.log(response.data[0].rulebook)
          if (response.data[0].rulebook !== null) {
            var fileAux = new File(['foo'], response.data[0].rulebook)
            this.file = fileAux
            this.file_aux = fileAux
          } else {
            this.file = null
            this.file_aux = null
          }
        })
        .catch(error => {
          console.log(error)
        })
    },
    changeNotifications () {
      this.loadingNotification = true
      const config = {
        method: 'PATCH',
        url: 'https://clubtest.intrare.app/auth/users/' + this.profileData.id + '/',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': this.language,
          'authorization': 'Token ' + store.getters.getToken
        },
        data: {
          'can_sms': this.canSMS,
          'can_email': this.canEmail
        }
      }
      axios(config)
        .then(ok => {
          this.loadingNotification = false
          this.getProfileData()
          this.$store.commit('snackbar_activate_true')
          this.$store.commit('snackbar_change_message', this.$t('notificacion_success'))
          this.dialog = false
          setTimeout(this.clear, 3000)
        })
        .catch(error => {
          this.loadingNotification = false
          if (error.response.status === 400 && typeof error.response.data === 'object' && error.response.data) {
            this.errores = error.response.data
          }
        })
    },
    changeRulebook () {
      this.loadingRulebook = true
      var bodyFormData = new FormData()

      bodyFormData.append('rulebook', this.file)

      const config = {
        method: 'PATCH',
        url: 'https://clubtest.intrare.app/housing_unit/update/rulebook/',
        headers: {
          'Content-Type': 'multipart/form-data',
          'Accept-Language': this.language,
          'authorization': 'Token ' + store.getters.getToken
        },
        data: bodyFormData
      }
      axios(config)
        .then(ok => {
          this.loadingRulebook = false
          this.getRulebook()
          this.$store.commit('snackbar_activate_true')
          this.$store.commit('snackbar_change_message', this.$t('rulebook_uploaded_successfully'))
          setTimeout(this.clear, 3000)
        })
        .catch(error => {
          this.loadingRulebook = false
          if (error.response.status === 400 && typeof error.response.data === 'object' && error.response.data) {
            this.errores = error.response.data
          }
        })
    },
    changeVacationMode () {
      this.errores = []
      this.loadingVacationMode = true
      let config = {}
      if (this.vacation_mode === true) {
        config = {
          method: 'PATCH',
          url: 'https://clubtest.intrare.app/auth/users/' + this.profileData.id + '/',
          headers: {
            'Content-Type': 'application/json',
            'Accept-Language': this.language,
            'authorization': 'Token ' + store.getters.getToken
          },
          data: {
            'vacation_mode': this.vacation_mode,
            'allow_access_visitors': this.allow_access_visitors,
            'allow_access_residents': this.allow_access_residents,
            'allow_access_workers': this.allow_access_workers,
            'vacation_mode_initial_date': this.date,
            'vacation_mode_final_date': this.dateExpiration
          }
        }
      } else {
        config = {
          method: 'PATCH',
          url: 'https://clubtest.intrare.app/auth/users/' + this.profileData.id + '/',
          headers: {
            'Content-Type': 'application/json',
            'Accept-Language': this.language,
            'authorization': 'Token ' + store.getters.getToken
          },
          data: {
            'vacation_mode': this.vacation_mode,
            'allow_access_visitors': this.allow_access_visitors,
            'allow_access_residents': this.allow_access_residents,
            'allow_access_workers': this.allow_access_workers
          }
        }
      }
      axios(config)
        .then(ok => {
          this.loadingVacationMode = false
          this.getProfileData()
          this.$store.commit('snackbar_activate_true')
          this.$store.commit('snackbar_change_message', this.$t('notificacion_success'))
          this.dialog = false
          setTimeout(this.clear, 3000)
        })
        .catch(error => {
          this.loadingVacationMode = false
          if (error.response.status === 400 && typeof error.response.data === 'object' && error.response.data) {
            this.errores = error.response.data
          }
        })
    },
    getCutingLogo () {
      // console.log('Datos de la imagen = ', this.imageFrontURL.size)
      const canvas = this.$refs.clipperImageLogo.clip() // call component's clip method
      this.resultLogoURL = canvas.toDataURL('image/jpeg', 0.2) // canvas->image
      // console.log(this.resultLogoURL)
      this.dialogImageLogo = false
    },
    changeWallpaper () {
      this.errores = []
      this.loadingWallpaper = true
      var bodyFormData = new FormData()
      var fileImageFront = null
      const newID = this.$uuid.v4()
      if (this.resultLogoURL !== '' && this.resultLogoURL.length > 6) {
        fileImageFront = this.dataURLtoFile(this.resultLogoURL, 'wallpaper_update' + newID + '.jpeg')
      }
      bodyFormData.append('wallpaper', fileImageFront)
      const config = {
        method: 'patch',
        url: 'https://clubtest.intrare.app/housing_unit/update/wallpaper/',
        headers: {
          'Content-Type': 'multipart/form-data',
          'Accept-Language': this.language,
          'authorization': 'Token ' + store.getters.getToken
        },
        data: bodyFormData
      }
      axios(config)
        .then(ok => {
          console.log(ok)
          this.loadingWallpaper = false
          this.$store.commit('snackbar_activate_true')
          this.$store.commit('snackbar_change_message', this.$t('cambiar_wallpaper_exito'))
          setTimeout(this.clear, 3000)
        })
        .catch(error => {
          this.loadingWallpaper = false
          if (error.response.status === 400 && typeof error.response.data === 'object' && error.response.data) {
            this.errores = error.response.data
          }
        })
    },
    dataURLtoFile (dataurl, filename) {
      console.log('Tamaño Imagen = ' + dataurl.length + ' caracteres.')
      var arr = dataurl.split(',')
      var mime = arr[0].match(/:(.*?);/)[1]
      var bstr = atob(arr[1])
      var n = bstr.length
      var u8arr = new Uint8Array(n)
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n)
      }
      return new File([u8arr], filename, { type: mime })
    },
    xOffset: function (pdf, text) {
      return (
        pdf.internal.pageSize.width / 2) - (pdf.getStringUnitWidth(text) * pdf.internal.getFontSize() / 2)
    },
    getImgFromUrl (logoUrl, callback) {
      var img = new Image()

      img.src = logoUrl
      img.onload = function () {
        callback(img)
      }
    },
    downloadMyAccessCode () {
      this.loadingWallpaper = true
      var urlAvatarAux = this.profileData.avatar
      var urlLogotipoAux = this.profileData.logo
      const toDataUrl = (url, callback) => {
        const xhr = new XMLHttpRequest()
        xhr.onload = () => {
          const reader = new FileReader()
          reader.onloadend = () => {
            callback(reader.result)
          }
          reader.readAsDataURL(xhr.response)
        }
        xhr.open('GET', url)
        xhr.responseType = 'blob'
        xhr.send()
      }
      toDataUrl('https://api.qrserver.com/v1/create-qr-code/?data=' + this.profileData.access_code + '&ecc=M&margin=20&size=400x400', (qrCodeImage) => {
        var pdf = new JSPDF('portrait', 'pt', 'letter')
        // Black square with rounded corners
        pdf.setDrawColor(0)
        pdf.setFillColor(255, 255, 255)
        pdf.roundedRect(180, 50, 250, 400, 5, 5, 'FD')
        // pdf.setFontStyle('bold')
        // pdf.text(infoInvitationSend.housing_unit_name,
        //   this.xOffset(pdf, infoInvitationSend.housing_unit_name),
        //   100)
        pdf.setFontStyle('normal')
        pdf.setFontSize(16)
        pdf.text(this.$t('residente'),
          this.xOffset(pdf, this.$t('residente')),
          115)
        pdf.setFontStyle('bold')
        pdf.setFontSize(13)
        pdf.text(this.profileData.first_name + ' ' + this.profileData.last_name,
          this.xOffset(pdf, this.profileData.first_name + ' ' + this.profileData.last_name),
          130)
        // pdf.setFontStyle('normal')
        // pdf.setFontSize(16)
        // pdf.text(this.$t('Anfitrion'),
        //   this.xOffset(pdf, this.$t('Anfitrion')),
        //   145)
        // pdf.setFontStyle('bold')
        // pdf.setFontSize(13)
        // pdf.text(infoInvitationSend.host_name,
        //   this.xOffset(pdf, infoInvitationSend.host_name),
        //   160)
        // pdf.setFontStyle('normal')
        // pdf.setFontSize(16)
        // pdf.text(this.$t('numero_casa'),
        //   this.xOffset(pdf, this.$t('numero_casa')),
        //   175)
        // pdf.setTextColor('#000000')
        // pdf.setFontStyle('bold')
        // pdf.setFontSize(13)
        // pdf.text(infoInvitationSend.house_address,
        //   this.xOffset(pdf, infoInvitationSend.house_address),
        //   190)
        // pdf.setTextColor('#000000')
        // pdf.setFontStyle('normal')
        // pdf.setFontSize(16)
        // pdf.text(this.$t('fecha_hora'),
        //   this.xOffset(pdf, this.$t('fecha_hora')),
        //   205)
        // pdf.setFontStyle('bold')
        // pdf.setFontSize(13)
        // pdf.text(infoInvitationSend.date + ' ' + infoInvitationSend.hour,
        //   this.xOffset(pdf, infoInvitationSend.date + ' ' + infoInvitationSend.hour),
        //   220)
        // pdf.setTextColor('#000000')
        // pdf.setFontStyle('normal')
        // pdf.setFontSize(16)
        // pdf.text(this.$t('fecha_expiracion'),
        //   this.xOffset(pdf, this.$t('fecha_expiracion')),
        //   235)
        pdf.setFontStyle('bold')
        pdf.setFontSize(13)
        pdf.text(this.$t('msg_mi_codigo'),
          this.xOffset(pdf, this.$t('msg_mi_codigo')),
          250)
        pdf.setFontSize(6)
        pdf.setFontStyle('normal')
        pdf.text('El único objetivo del presente gafete es la de validar el acceso del portador',
          this.xOffset(pdf, 'El único objetivo del presente gafete es la de validar el acceso del portador'),
          410)
        pdf.text('mediante las reglas de operación del sistema Intrare Residencia. No existe',
          this.xOffset(pdf, 'mediante las reglas de operación del sistema Intrare Residencia. No existe'),
          418)
        pdf.text('relación laboral alguna entre el portador de este gafete ni con la empresa',
          this.xOffset(pdf, 'relación laboral alguna entre el portador de este gafete ni con la empresa'),
          426)
        pdf.text('Intrare Residencia ni con el Fraccionamiento.',
          this.xOffset(pdf, 'Intrare Residencia ni con el Fraccionamiento.'),
          434)
        // pdf.setLineWidth(4.0)
        // pdf.setDrawColor('#0000FF')
        // pdf.setFillColor('#FFFFFF')
        // pdf.roundedRect(212, 257, 186, 186, 5, 5, 'FD')
        // console.log(qrCodeImage); // qrCodeImage is the base64 string
        pdf.addImage(qrCodeImage, 'WEBP', 230, 254, 150, 150)
        toDataUrl(urlLogotipoAux, (logoGuest) => {
          pdf.addImage(logoGuest, 'WEBP', 190, 60, 50, 50)
          this.getImgFromUrl(require('../assets/residencial_mx.png'), function (logoSystem) {
            pdf.addImage(logoSystem, 'WEBP', 370, 60, 50, 50)
            toDataUrl(urlAvatarAux, (avatar) => {
              pdf.addImage(avatar, 'WEBP', 265, 150, 80, 80)
              pdf.save('gafete.pdf')
            })
          })
        })
        this.loadingWallpaper = false
      })
    }
  }
}
</script>

<style scoped>
.settings-page {
  width: 100%;
  max-width: 1320px;
  margin: 0 auto;
}

.settings-hero {
  background: linear-gradient(125deg, var(--v-primary-base), #263b6a);
}

.settings-hero__description {
  max-width: 720px;
  opacity: 0.88;
}

.settings-card {
  height: 100%;
  border-color: #e4e8ef !important;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.settings-card:hover {
  box-shadow: 0 8px 24px rgba(34, 48, 74, 0.08) !important;
}

.section-header {
  min-height: 82px;
  padding: 18px 20px;
  display: flex;
  align-items: center;
}

.setting-option {
  min-height: 72px;
  padding: 12px 16px;
  border: 1px solid #e8ebf0;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: #fafbfc;
}

.wallpaper-preview {
  overflow: hidden;
  border: 1px solid #e2e6ec;
  border-radius: 10px;
  background: #f5f7fa;
}

.wallpaper-empty {
  height: 260px;
}

.qr-frame {
  width: 258px;
  padding: 13px;
  border: 1px solid #e3e7ed;
  border-radius: 16px;
  background: white;
  box-shadow: 0 8px 24px rgba(30, 42, 68, 0.08);
}

.access-options {
  padding: 4px 16px 16px;
  border: 1px solid #e8ebf0;
  border-radius: 10px;
  background: #fafbfc;
}

.cropper-container {
  min-height: 360px;
}

@media (max-width: 599px) {
  .section-header {
    padding: 16px;
  }

  .setting-option {
    padding: 12px;
  }

  .settings-hero .v-avatar {
    align-self: flex-start;
  }
}
</style>
